import React from "react";
import {
  Container,
  Segment,
  Loader,
  Button,
  Icon,
  Image,
  Message,
  Accordion,
  Divider,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import TestataPagina from "../common/TestataPagina";
import Preferenze from "./Preferenze";
import Calendar from "./Calendar";
import TimeChipSelector from "./TimeChipSelector";
import GrigliaMaestri from "./GrigliaMaestri";
import PupilDataModal from "./PupilDataModal";

const PrivateNewMain2Mobile = ({
  // Redux props
  datiScuola,
  datiScuolaOld,
  loading,
  // State from parent
  selectedArea,
  setSelectedArea,
  selectedSpecialita,
  setSelectedSpecialita,
  selectedLivello,
  setSelectedLivello,
  selectedLingua,
  setSelectedLingua,
  selectedNumAllevi,
  setSelectedNumAllevi,
  isPreferenzeValid,
  hasBeenInitialized,
  dateNonDisponibili,
  selectedDates,
  nomeMaestro,
  maestroData,
  idMaestro,
  setIdMaestro,
  disponibilità,
  firstDayTimeSlots,
  timeRangeFirstDay,
  setTimeRangeFirstDay,
  timeRangesOtherDays,
  prezziLezioni,
  isMaestroMode,
  setIsMaestroMode,
  showMaestriModal,
  isConfirmEnabled,
  showPupilModal,
  isLoadingUnavailableDays,
  // Handlers from parent
  handleRecalculate,
  handleOpenMaestriModal,
  handleCloseMaestriModal,
  handleSelectMaestro,
  handleFirstDaySlotClick,
  handleMainCalendarDateChange,
  fetchBestAvailability,
  createTimeSlotsFromDisponibilita,
  createOtherDaySlotClickHandler,
  clearOtherDayTimeRange,
  handleConfirmLessons,
  handlePupilDataConfirm,
  handlePupilDataCancel,
}) => {
  const { t } = useTranslation();
  const [activeAccordionIndex, setActiveAccordionIndex] = React.useState(0);
  const [hasAutoOpenedStep2, setHasAutoOpenedStep2] = React.useState(false);
  const [hasAutoOpenedStep3, setHasAutoOpenedStep3] = React.useState(false);

  const handleAccordionClick = (e, titleProps) => {
    const { index } = titleProps;
    const newIndex = activeAccordionIndex === index ? -1 : index;
    setActiveAccordionIndex(newIndex);
  };

  // Auto-open next step when previous step is completed
  React.useEffect(() => {
    // Step 1 completed -> open Step 2 (only once)
    if (
      isPreferenzeValid &&
      activeAccordionIndex === 0 &&
      !hasAutoOpenedStep2
    ) {
      setActiveAccordionIndex(1);
      setHasAutoOpenedStep2(true);
    }
  }, [isPreferenzeValid, activeAccordionIndex, hasAutoOpenedStep2]);

  React.useEffect(() => {
    // When disponibilità is available, open Step 3 (only once)
    if (disponibilità?.length > 0 && !hasAutoOpenedStep3) {
      setActiveAccordionIndex(2);
      setHasAutoOpenedStep3(true);
    }
  }, [disponibilità, hasAutoOpenedStep3]);

  // Handler for "Next" button in calendar step
  const handleCalendarNext = () => {
    setActiveAccordionIndex(2);
  };

  // Handler for recalculate button
  const handleRecalculateAndProceed = () => {
    handleRecalculate();
    // Move to step 2 after recalculating
    setActiveAccordionIndex(1);
  };

  // Function to render the lesson summary
  const renderLessonSummary = () => {
    if (!prezziLezioni) return null;

    const orderedLessonKeys = Object.keys(prezziLezioni).sort();

    let totalListPrice = 0;
    let totalActualPrice = 0;
    let totalDeposit = 0;
    const currency = Array.isArray(prezziLezioni[orderedLessonKeys[0]])
      ? prezziLezioni[orderedLessonKeys[0]][0]?.currency || "EUR"
      : prezziLezioni[orderedLessonKeys[0]]?.currency || "EUR";

    orderedLessonKeys.forEach((lessonKey) => {
      const lessonPrice = prezziLezioni[lessonKey];
      if (Array.isArray(lessonPrice) && lessonPrice.length >= 3) {
        totalListPrice += lessonPrice[0]?.amount ?? 0;
        totalActualPrice += lessonPrice[1]?.amount ?? 0;
        totalDeposit += lessonPrice[2]?.amount ?? 0;
      }
    });

    return (
      <div
        style={{
          backgroundColor: "white",
          border: "2px solid #2185d0",
          borderRadius: "10px",
          padding: "15px",
          margin: "20px 0",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <h3
          style={{
            color: "#2185d0",
            marginTop: 0,
            marginBottom: "15px",
            textAlign: "center",
            borderBottom: "1px solid #e0e0e0",
            paddingBottom: "10px",
          }}
        >
          {t("riepilogo_lezioni") || "Riepilogo lezioni"}
        </h3>

        <div style={{ marginBottom: "15px" }}>
          {orderedLessonKeys.map((lessonKey) => {
            const lessonPrice = prezziLezioni[lessonKey];
            const [datePart, startTime, endTime] = lessonKey.split("_");
            const formattedStartTime = `${startTime.slice(
              0,
              2
            )}:${startTime.slice(2)}`;
            const formattedEndTime = `${endTime.slice(0, 2)}:${endTime.slice(
              2
            )}`;

            let listPrice = null;
            let actualPrice = null;
            let priceDisplay = null;

            if (Array.isArray(lessonPrice) && lessonPrice.length >= 2) {
              listPrice = lessonPrice[0]?.amount;
              actualPrice = lessonPrice[1]?.amount;
              const priceCurrency = lessonPrice[0]?.currency || "EUR";

              if (actualPrice === null || actualPrice === undefined) {
                priceDisplay = null;
              } else if (listPrice !== actualPrice) {
                priceDisplay = (
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#2185d0",
                      fontSize: "14px",
                    }}
                  >
                    <span
                      style={{
                        textDecoration: "line-through",
                        color: "#999",
                        marginRight: "8px",
                      }}
                    >
                      {listPrice.toFixed(2)} {priceCurrency}
                    </span>
                    <span>
                      {actualPrice.toFixed(2)} {priceCurrency}
                    </span>
                  </div>
                );
              } else {
                priceDisplay = (
                  <div
                    style={{
                      fontWeight: "bold",
                      color: "#2185d0",
                      fontSize: "14px",
                    }}
                  >
                    {actualPrice.toFixed(2)} {priceCurrency}
                  </div>
                );
              }
            }

            return (
              <div
                key={lessonKey}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "10px 0",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                    {moment(datePart, "YYYY-MM-DD").format("DD/MM/YYYY")}
                  </div>
                  {formattedStartTime === "00:00" && (
                    <div style={{ fontSize: "14px", color: "red" }}>
                      {t("selezionare_orario") || "SELEZIONARE ORARIO"}
                    </div>
                  )}
                  {formattedStartTime !== "00:00" && (
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      {formattedStartTime} - {formattedEndTime}
                    </div>
                  )}
                </div>
                {priceDisplay}
              </div>
            );
          })}
        </div>

        <div
          style={{
            borderTop: "2px solid #2185d0",
            paddingTop: "10px",
            marginBottom: "15px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: totalDeposit !== totalActualPrice ? "10px" : "0",
            }}
          >
            <span style={{ fontWeight: "bold", fontSize: "16px" }}>
              {t("totale") || "Totale"}
            </span>
            <span
              style={{ fontWeight: "bold", fontSize: "18px", color: "#2185d0" }}
            >
              {totalListPrice !== totalActualPrice && (
                <span
                  style={{
                    textDecoration: "line-through",
                    color: "#999",
                    marginRight: "8px",
                  }}
                >
                  {totalListPrice.toFixed(2)} {currency}
                </span>
              )}
              <span>
                {totalActualPrice.toFixed(2)} {currency}
              </span>
            </span>
          </div>

          {totalDeposit !== totalActualPrice && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: "14px", color: "#666" }}>
                {t("anticipo_versare") || "Anticipo da versare"}
              </span>
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: "#2185d0",
                }}
              >
                {totalDeposit.toFixed(2)} {currency}
              </span>
            </div>
          )}
        </div>

        <Button
          primary
          fluid
          size="large"
          onClick={handleConfirmLessons}
          disabled={!isConfirmEnabled}
        >
          {t("conferma") || "Conferma"}
        </Button>
      </div>
    );
  };

  if (loading) {
    return (
      <Container>
        <div className="head-offset"></div>
        <TestataPagina
          titolo={t("lezioni_private")}
          icon="user"
          sottoTitolo={t("scegli_preferenze_private")}
        />
        <Segment>
          <Loader active inline="centered" />
        </Segment>
      </Container>
    );
  }

  return (
    <Container style={{ paddingBottom: "20px" }}>
      <div className="head-offset"></div>
      <TestataPagina
        titolo={t("lezioni_private")}
        icon="user"
        sottoTitolo={t("scegli_preferenze_private")}
      />

      <Accordion fluid styled>
        {/* Step 1: Preferences */}
        <Accordion.Title
          active={activeAccordionIndex === 0}
          index={0}
          onClick={handleAccordionClick}
        >
          <Icon name="dropdown" />
          <strong>1. {t("preferenze") || "Preferenze"}</strong>
          {isPreferenzeValid && (
            <Icon
              name="check circle"
              color="green"
              style={{ float: "right", marginTop: "3px" }}
            />
          )}
        </Accordion.Title>
        <Accordion.Content active={activeAccordionIndex === 0}>
          <Preferenze
            datiScuola={datiScuola}
            selectedArea={selectedArea}
            setSelectedArea={setSelectedArea}
            selectedSpecialita={selectedSpecialita}
            setSelectedSpecialita={setSelectedSpecialita}
            selectedLivello={selectedLivello}
            setSelectedLivello={setSelectedLivello}
            selectedLingua={selectedLingua}
            setSelectedLingua={setSelectedLingua}
            selectedNumAllevi={selectedNumAllevi}
            setSelectedNumAllevi={setSelectedNumAllevi}
          />

          {/* Maestro Selection */}
          <div style={{ marginTop: "1rem" }}>
            <label style={{ display: "block", marginBottom: "0.5rem" }}>
              <Icon name="graduation cap" style={{ marginRight: "0.5rem" }} />
              {t("maestro") || "Maestro"}{" "}
              <span
                style={{
                  fontStyle: "italic",
                  color: "#999",
                  fontSize: "0.9em",
                }}
              >
                ({t("opzionale") || "opzionale"})
              </span>
            </label>
            {!isMaestroMode ? (
              <Button
                fluid
                onClick={handleOpenMaestriModal}
                style={{
                  textAlign: "left",
                  justifyContent: "flex-start",
                  backgroundColor: "#fff",
                  color: "rgba(0,0,0,.87)",
                  border: "1px solid rgba(34,36,38,.15)",
                }}
              >
                <Icon name="search" style={{ marginRight: "0.5rem" }} />
                {t("scegli_maestro") || "Scegli un maestro specifico"}
              </Button>
            ) : (
              <div
                style={{
                  border: "1px solid rgba(34, 36, 38, 0.15)",
                  borderRadius: "0.28571429rem",
                  padding: "1rem",
                  backgroundColor: "#f8f9fa",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "0.5rem",
                  }}
                >
                  <Image
                    src={
                      maestroData?.picture
                        ? `https://skiwork.blob.core.windows.net/webimg/${datiScuolaOld?.codiceScuola?.toUpperCase()}/Maestri/${
                            maestroData.picture
                          }`
                        : maestroData?.sesso === "F"
                        ? require("../../assets/images/people/AvatarMaestra.jpg")
                        : require("../../assets/images/people/AvatarMaestro.jpg")
                    }
                    avatar
                    size="mini"
                    onError={(e) => {
                      e.target.src =
                        maestroData?.sesso === "F"
                          ? require("../../assets/images/people/AvatarMaestra.jpg")
                          : require("../../assets/images/people/AvatarMaestro.jpg");
                    }}
                    style={{ marginRight: "0.75rem" }}
                  />
                  <span style={{ fontWeight: "bold", flex: 1 }}>
                    {nomeMaestro}
                  </span>
                </div>
                <Button
                  fluid
                  secondary
                  size="small"
                  onClick={() => {
                    setIsMaestroMode(false);
                    setIdMaestro(-1);
                  }}
                >
                  <Icon name="close" />
                  {t("rimuovi_selezione") || "Rimuovi selezione"}
                </Button>
              </div>
            )}
          </div>

          {selectedArea &&
            selectedSpecialita &&
            selectedLivello &&
            selectedLingua &&
            selectedNumAllevi > 0 && (
              <Message
                warning
                style={{
                  marginTop: "1.5rem",
                  padding: "1.5rem",
                  textAlign: "center",
                }}
              >
                <Message.Header
                  style={{ marginBottom: "1rem", fontSize: "1.1em" }}
                >
                  <Icon name="attention" />
                  {t("aggiorna_preferenze") || "Aggiorna le tue preferenze"}
                </Message.Header>
                <p style={{ marginBottom: "1rem" }}>
                  {t("clicca_ricalcola_per_procedere") ||
                    "Clicca il pulsante qui sotto per procedere con la prenotazione"}
                </p>
                <Button
                  primary
                  size="large"
                  fluid
                  onClick={handleRecalculateAndProceed}
                  style={{
                    fontSize: "1.1em",
                    padding: "1rem",
                  }}
                >
                  <Icon name="refresh" />
                  {t("ricalcola_disponibilita") || "Ricalcola disponibilità"}
                </Button>
              </Message>
            )}
        </Accordion.Content>

        {/* Step 2: Calendar */}
        <Accordion.Title
          active={activeAccordionIndex === 1}
          index={1}
          onClick={handleAccordionClick}
          disabled={!isPreferenzeValid}
        >
          <Icon name="dropdown" />
          <strong>2. {t("calendario") || "Calendario"}</strong>
          {selectedDates?.length > 0 && (
            <Icon
              name="check circle"
              color="green"
              style={{ float: "right", marginTop: "3px" }}
            />
          )}
        </Accordion.Title>
        <Accordion.Content active={activeAccordionIndex === 1}>
          {activeAccordionIndex === 1 && (
            <>
              {isLoadingUnavailableDays ? (
                <Segment
                  style={{
                    minHeight: "300px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <Loader active inline="centered" size="large" />
                    <p style={{ marginTop: "20px", color: "#666" }}>
                      {t("caricamento_disponibilita") ||
                        "Caricamento disponibilità..."}
                    </p>
                  </div>
                </Segment>
              ) : (
                <>
                  <Calendar
                    key="calendar-mobile"
                    isPreferenzeValid={isPreferenzeValid}
                    dateNonDisponibili={dateNonDisponibili}
                    selectedDates={selectedDates}
                    onDateChange={handleMainCalendarDateChange}
                  />
                  {selectedDates?.length > 0 && (
                    <Button
                      primary
                      fluid
                      size="large"
                      onClick={handleCalendarNext}
                      style={{ marginTop: "20px" }}
                    >
                      <Icon name="arrow right" /> {t("avanti") || "Avanti"}
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </Accordion.Content>

        {/* Step 3: Time Selection */}
        <Accordion.Title
          active={activeAccordionIndex === 2}
          index={2}
          onClick={handleAccordionClick}
          disabled={!isPreferenzeValid || selectedDates?.length === 0}
        >
          <Icon name="dropdown" />
          <strong>3. {t("orari") || "Orari"}</strong>
          {disponibilità?.length > 0 && (
            <Icon
              name="check circle"
              color="green"
              style={{ float: "right", marginTop: "3px" }}
            />
          )}
        </Accordion.Title>
        <Accordion.Content active={activeAccordionIndex === 2}>
          {isPreferenzeValid &&
            selectedDates?.length > 0 &&
            disponibilità?.length === 0 && (
              <>
                <h3 style={{ textAlign: "center" }}>
                  {t("orario_primo_giorno") ||
                    "Orario lezioni per il primo giorno"}
                </h3>
                <TimeChipSelector
                  timeSlots={firstDayTimeSlots}
                  tempTimeRange={timeRangeFirstDay}
                  onTimeSlotClick={handleFirstDaySlotClick}
                  onClearTimeRange={() => setTimeRangeFirstDay(null)}
                  showConfirmButton={true}
                  onTimeRangeConfirm={async () => {
                    await fetchBestAvailability();
                  }}
                  minHour={datiScuola.orarioDalle}
                  maxHour={datiScuola.orarioAlle}
                  minOreAcquistabili={datiScuola.minOreAcquistabili}
                  maxOreAcquistabiliAlGiorno={
                    datiScuola.maxOreAcquistabiliAlGiorno
                  }
                  permettiEccezioneMinOreInizio={
                    datiScuola.permettiEccezioneMinOreInizio
                  }
                  permettiEccezioneMinOreFine={
                    datiScuola.permettiEccezioneMinOreFine
                  }
                />
              </>
            )}

          {disponibilità?.length > 0 && (
            <div>
              <h3 style={{ textAlign: "center", marginBottom: "20px" }}>
                {t("conferma_orario_giornate") ||
                  "Conferma l'orario delle lezioni per ogni giorno selezionato"}
              </h3>
              {(() => {
                const groupedByDate = disponibilità.reduce((acc, disp) => {
                  const { data, dalle, alle } = disp;
                  if (!acc[data]) {
                    acc[data] = [];
                  }
                  acc[data].push({ dalle, alle });
                  return acc;
                }, {});

                return Object.entries(groupedByDate).map(
                  ([data, timeSlots]) => {
                    const combinedTimeSlots = timeSlots.flatMap((slot) =>
                      createTimeSlotsFromDisponibilita(slot.dalle, slot.alle)
                    );
                    const tempRange = timeRangesOtherDays[data] || null;

                    return (
                      <div key={data} style={{ marginBottom: "20px" }}>
                        <TimeChipSelector
                          data={moment(data).format("DD/MM/YYYY")}
                          timeSlots={combinedTimeSlots}
                          tempTimeRange={tempRange}
                          onTimeSlotClick={createOtherDaySlotClickHandler(data)}
                          onClearTimeRange={() => clearOtherDayTimeRange(data)}
                          showConfirmButton={false}
                          minHour={datiScuola.orarioDalle}
                          maxHour={datiScuola.orarioAlle}
                          minOreAcquistabili={datiScuola.minOreAcquistabili}
                          maxOreAcquistabiliAlGiorno={
                            datiScuola.maxOreAcquistabiliAlGiorno
                          }
                          permettiEccezioneMinOreInizio={
                            datiScuola.permettiEccezioneMinOreInizio
                          }
                          permettiEccezioneMinOreFine={
                            datiScuola.permettiEccezioneMinOreFine
                          }
                        />
                        <Divider />
                      </div>
                    );
                  }
                );
              })()}
            </div>
          )}
        </Accordion.Content>
      </Accordion>

      {/* Lesson Summary */}
      {renderLessonSummary()}

      {/* Maestri Selection Modal */}
      <GrigliaMaestri
        isOpen={showMaestriModal}
        onClose={handleCloseMaestriModal}
        onSelectMaestro={handleSelectMaestro}
        idScuola={datiScuolaOld?.id}
        codScuola={datiScuolaOld?.codiceScuola}
      />

      {/* Pupil Data Modal */}
      <PupilDataModal
        isOpen={showPupilModal}
        onClose={handlePupilDataCancel}
        onConfirm={handlePupilDataConfirm}
        numberOfPupils={selectedNumAllevi}
      />
    </Container>
  );
};

export default PrivateNewMain2Mobile;
